// Making a schedule, and changing one.
//
// The three-step flow is the prototype's, step for step: name, then colour and
// icon, then the first thing in it. It is three screens rather than one form
// because the first sixty seconds of an empty account run through it, and one
// long form of eight controls is where a general audience stops.
//
// A schedule is never made empty. The last step is the first thing in it, and
// `useEditor.createSchedule` undoes the schedule if that write fails - a
// schedule with nothing in it appears on no day, because a day leaves out the
// schedules with nothing due, while still counting against the cap.

import { useState } from 'react';
import type { CSSProperties } from 'react';
import { capNotice } from '../day/day-model.ts';
import { Icon } from '../day/Icon.tsx';
import { useMode } from '../shell/theme.ts';
import { RepeatField } from './RepeatField.tsx';
import {
  Chips,
  Field,
  FieldGroup,
  IconGrid,
  Notice,
  Sheet,
  StepLabel,
  Swatches,
} from './Sheet.tsx';
import {
  hueIn,
  ICONS,
  NAME_SUGGESTIONS,
  PALETTE,
  repeatIsComplete,
  thingCount,
  toggleDay,
  toRule,
} from './edit-model.ts';
import type { EditableSchedule, Repeat } from './edit-model.ts';
import type { NewScheduleInput } from './useEditor.ts';

const DEFAULT_COLOR = PALETTE[0]?.hex ?? '#2F5FBF';
const DEFAULT_SYMBOL = ICONS[0] ?? 'circle';

const SUGGESTIONS = NAME_SUGGESTIONS.map((name) => ({ key: name, label: name }));

/** The card head as it will look, so colour and icon are chosen against the thing. */
function Preview({ name, colorHex, symbol }: { name: string; colorHex: string; symbol: string }) {
  // "How it will look" has to mean it, so the preview paints the mode-correct
  // hue exactly as the card on the day will.
  const mode = useMode();
  return (
    <div className="preview" style={{ '--c': hueIn(colorHex, mode) } as CSSProperties}>
      <div className="pl">How it will look</div>
      <div className="preview-head">
        <span className="chip">
          <Icon name={symbol} />
        </span>
        <h2>{name}</h2>
        <span className="card-count tnum">0/1</span>
      </div>
      <div className="bar thin sec">
        <i style={{ width: '0%' }} />
      </div>
    </div>
  );
}

function ColorAndIcon({
  colorHex,
  symbol,
  onColor,
  onSymbol,
}: {
  colorHex: string;
  symbol: string;
  onColor: (hex: string) => void;
  onSymbol: (symbol: string) => void;
}) {
  return (
    <>
      <FieldGroup label="Colour">
        <Swatches options={PALETTE} chosen={colorHex} onChoose={onColor} />
      </FieldGroup>
      <FieldGroup label="Icon">
        <IconGrid options={ICONS} chosen={symbol} onChoose={onSymbol} />
      </FieldGroup>
    </>
  );
}

/** Why a write did not happen, in the sheet, beside the button that did not work. */
function Problem({ problem }: { problem: string | null }) {
  return problem === null ? null : (
    <p className="sheetproblem" role="alert">
      {problem}
    </p>
  );
}

/**
 * At the cap: what the limit is, and the way to act on it.
 *
 * THE WAY TO ACT ON IT IS A SCREEN, NOT A LIST IN HERE. This sheet used to
 * carry the account's schedules itself, because a schedule was otherwise only
 * openable from its card on the day and the day leaves out the ones with
 * nothing due - so the schedule somebody wants to archive at the cap is, by
 * definition, the one they are not using and therefore the one their day does
 * not draw. That was right about the hole and wrong about where to put the
 * door: opening a schedule from a list inside a sheet REPLACED the sheet, so
 * Cancel returned to the day and the list you came from was gone. Restoring it
 * would have meant a "where did this come from" marker carried through the
 * sheet state - a half-navigation inside a modal, which is the thing the
 * navigation shell exists to replace.
 *
 * So the list lives on the Settings screen now, where it is a place rather than
 * a step: it holds the archived schedules too, it can put one back, and Cancel
 * out of a schedule opened from it lands on it because it is simply still
 * there, behind the sheet. This sheet points at it.
 *
 * Both figures in the sentence came with the day payload, so nothing here waits
 * on a read and nothing here writes the cap down (AGENTS.md).
 */
function AtCapSheet({
  notice,
  onManageSchedules,
  onClose,
}: {
  notice: string;
  onManageSchedules: () => void;
  onClose: () => void;
}) {
  return (
    <Sheet
      title="Schedule limit"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Close
          </button>
          <button className="btn primary" onClick={onManageSchedules}>
            Your schedules
          </button>
        </>
      }
    >
      <h3>That is as many schedules as there are.</h3>
      <p className="sub">The limit is the same for everyone, and it is not for sale.</p>
      <Notice>{notice}</Notice>
      <p className="finetext">
        It exists so your day can always be read at a glance. <b>Your schedules</b> lists every one
        you have, including the ones today has nothing due in - which is where the one you have
        stopped using will be. Archiving keeps everything you have already finished, and you can put
        an archived schedule back.
      </p>
    </Sheet>
  );
}

interface NewProps {
  /** The ACCOUNT's active schedules and the cap, both from the day payload. */
  schedules: { total: number; cap: number };
  /** The day a one-off first thing would land on, in words. */
  onceOn: string;
  busy: boolean;
  problem: string | null;
  onCreate: (input: NewScheduleInput) => void;
  /** Leaves for the schedules list in Settings; only the cap sheet uses it. */
  onManageSchedules: () => void;
  onClose: () => void;
}

export function NewScheduleSheet({
  schedules,
  onceOn,
  busy,
  problem,
  onCreate,
  onManageSchedules,
  onClose,
}: NewProps) {
  const [step, setStep] = useState(1);
  const [name, setName] = useState('');
  const [colorHex, setColorHex] = useState(DEFAULT_COLOR);
  const [symbol, setSymbol] = useState(DEFAULT_SYMBOL);
  const [first, setFirst] = useState('');
  const [repeat, setRepeat] = useState<Repeat>('every');
  const [days, setDays] = useState<number[]>([]);
  const [evening, setEvening] = useState(false);

  // The cap, said BEFORE the form rather than after the button - which is what
  // the approved design does, and the only version that is not a person filling
  // in three screens to be refused at the end. Both figures come from the
  // payload: `cap` is `section_cap()` in the database, the same function the
  // trigger enforces, so nothing here ever has to know the number (AGENTS.md).
  const atCap = capNotice(schedules);
  if (atCap !== null) {
    return <AtCapSheet notice={atCap} onManageSchedules={onManageSchedules} onClose={onClose} />;
  }

  if (step === 1) {
    return (
      <Sheet
        title="New schedule"
        onClose={onClose}
        footer={
          <>
            <button className="btn" onClick={onClose}>
              Cancel
            </button>
            <button
              className="btn primary"
              disabled={name.trim() === ''}
              onClick={() => setStep(2)}
            >
              Next
            </button>
          </>
        }
      >
        <StepLabel>Step 1 of 3</StepLabel>
        <h3>What do you want to keep track of?</h3>
        <p className="sub">
          A schedule is any part of your life you want to see on its own. Work and School are two.
          Anything you can name is another.
        </p>
        <Field label="Name">
          {(id) => (
            <input
              id={id}
              type="text"
              autoFocus
              autoComplete="off"
              placeholder="Music practice"
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          )}
        </Field>
        <FieldGroup label="Or start from one of these">
          <Chips options={SUGGESTIONS} chosen={[name]} onChoose={setName} />
        </FieldGroup>
      </Sheet>
    );
  }

  if (step === 2) {
    return (
      <Sheet
        title="Colour and icon"
        onClose={onClose}
        footer={
          <>
            <button className="btn" onClick={() => setStep(1)}>
              Back
            </button>
            <button className="btn primary" onClick={() => setStep(3)}>
              Next
            </button>
          </>
        }
      >
        <StepLabel>Step 2 of 3</StepLabel>
        <h3>Give it a look</h3>
        <p className="sub">
          Colour is how you tell your schedules apart at a glance. Pick anything you like - none of
          this is locked.
        </p>
        <Preview name={name.trim()} colorHex={colorHex} symbol={symbol} />
        <ColorAndIcon
          colorHex={colorHex}
          symbol={symbol}
          onColor={setColorHex}
          onSymbol={setSymbol}
        />
      </Sheet>
    );
  }

  const ready = first.trim() !== '' && repeatIsComplete(repeat, days);

  return (
    <Sheet
      title="First thing"
      onClose={onClose}
      footer={
        <>
          <button className="btn" disabled={busy} onClick={() => setStep(2)}>
            Back
          </button>
          <button
            className="btn primary"
            disabled={!ready || busy}
            onClick={() =>
              onCreate({
                name: name.trim(),
                colorHex,
                symbol,
                first: { title: first.trim(), rule: toRule(repeat, days), evening },
              })
            }
          >
            {busy ? 'Creating…' : 'Create schedule'}
          </button>
        </>
      }
    >
      <StepLabel>Step 3 of 3</StepLabel>
      <h3>Add the first thing</h3>
      <p className="sub">You can add more later, and change any of this at any time.</p>

      <Field label="What is it?">
        {(id) => (
          <input
            id={id}
            type="text"
            autoFocus
            autoComplete="off"
            placeholder="Scales - 15 min"
            value={first}
            onChange={(event) => setFirst(event.target.value)}
          />
        )}
      </Field>

      <RepeatField
        repeat={repeat}
        days={days}
        onRepeat={setRepeat}
        onToggleDay={(day) => setDays((chosen) => toggleDay(chosen, day))}
        onceOn={onceOn}
      />

      <EveningToggle evening={evening} onChange={setEvening} />
      <Problem problem={problem} />
    </Sheet>
  );
}

/**
 * "This evening" is a quiet marker on the meta line, not a bucket (AGENTS.md),
 * so it is one switch here rather than a time field or a second list to file
 * things into.
 */
export function EveningToggle({
  evening,
  onChange,
}: {
  evening: boolean;
  onChange: (evening: boolean) => void;
}) {
  return (
    <div className="field">
      {/* The label WRAPS the checkbox rather than pointing at it, so the whole
          row is the target - and it is the only label on this control, so what
          is announced is one sentence rather than a heading and then a
          sentence. That is why this does not go through `Field`. */}
      <label className="switchrow">
        <input
          type="checkbox"
          checked={evening}
          onChange={(event) => onChange(event.target.checked)}
        />
        <span>
          <b>This evening</b>A quiet marker on the row. It stays in this schedule; it just says so.
        </span>
      </label>
    </div>
  );
}

interface EditProps {
  /**
   * The schedule, from either of the two places it can be opened from. What is
   * due in it today is a number when a day answered that question and null when
   * none did - see `EditableSchedule`.
   */
  schedule: EditableSchedule;
  busy: boolean;
  problem: string | null;
  onSave: (id: string, patch: { name: string; colorHex: string; symbol: string }) => void;
  onArchive: (id: string, name: string) => void;
  onDeleteForever: (id: string, name: string) => void;
  onClose: () => void;
}

export function EditScheduleSheet({
  schedule,
  busy,
  problem,
  onSave,
  onArchive,
  onDeleteForever,
  onClose,
}: EditProps) {
  const [name, setName] = useState(schedule.name);
  const [colorHex, setColorHex] = useState(schedule.colorHex);
  const [symbol, setSymbol] = useState(schedule.symbol);
  // Deleting forever is the one act with no undo, so it asks. A confirm is the
  // honest substitute for an Undo the API cannot provide - offering one that
  // did nothing would be worse than offering none.
  const [confirming, setConfirming] = useState(false);

  const trimmed = name.trim();

  return (
    <Sheet
      title={schedule.name}
      onClose={onClose}
      footer={
        <>
          <button className="btn" disabled={busy} onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn primary"
            disabled={trimmed === '' || busy}
            onClick={() => onSave(schedule.id, { name: trimmed, colorHex, symbol })}
          >
            {busy ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      <h3>{schedule.name}</h3>
      {/* Only when a day answered it. Reached from the schedules list in
          Settings there is no day behind this schedule, and "0 things in this
          schedule today" for one that runs every Sunday would be a made-up
          figure. */}
      {schedule.dueOnDay !== null && (
        <p className="sub">{thingCount(schedule.dueOnDay)} in this schedule today.</p>
      )}

      <Field label="Name">
        {(id) => (
          <input
            id={id}
            type="text"
            autoComplete="off"
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        )}
      </Field>

      <ColorAndIcon
        colorHex={colorHex}
        symbol={symbol}
        onColor={setColorHex}
        onSymbol={setSymbol}
      />

      {/* Both destructive actions in one place, with the difference between
          them said rather than implied by two similar-looking buttons.
          Completions outlive both - `completion.routine_id` carries no foreign
          key exactly so that removing a routine cannot rewrite what your year
          says you did (AGENTS.md) - so "deleting removes the history too",
          which the prototype says here, is not true of this implementation and
          is not repeated. */}
      <Notice>
        Archiving takes a schedule off your day and keeps it, so you can put it back. Deleting
        removes the schedule and everything in it for good. Either way, the days you have already
        finished still count in Progress.
      </Notice>

      <div className="rowbtns">
        <button
          className="btn"
          disabled={busy}
          onClick={() => onArchive(schedule.id, schedule.name)}
        >
          <Icon name="archive" />
          Archive
        </button>
        {confirming ? (
          <button
            className="btn danger"
            disabled={busy}
            onClick={() => onDeleteForever(schedule.id, schedule.name)}
          >
            <Icon name="trash" />
            Yes, delete forever
          </button>
        ) : (
          <button className="btn danger" disabled={busy} onClick={() => setConfirming(true)}>
            <Icon name="trash" />
            Delete forever
          </button>
        )}
      </div>

      {confirming && (
        <p className="sheetproblem" role="alert">
          &quot;{schedule.name}&quot; and everything in it will be gone, and this one cannot be
          undone.
        </p>
      )}

      <Problem problem={problem} />
    </Sheet>
  );
}
